import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Edit2, Truck, Handshake, Trash2, Plus, Calculator, ArrowRight, Scale, Ban, X, Undo2 } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import StatusBadge from "@/components/masterOrder/StatusBadge";
import DateField from "@/components/masterOrder/DateField";
import CurrencyField from "@/components/masterOrder/CurrencyField";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import {
  getOrder, updateOrder, getClient, listOrderItems, listProductTypes,
  createOrderItem, updateOrderItem, deleteOrderItem, createProductType,
  getOrderPaymentsSummary,
  getCostingCurrent, createCostingDraft, orderCostingSummary,
  COSTING_STATUS_LABELS, costingStatusTone, mapCostingError,
  BUSINESS_STATUSES, LIFECYCLE_STATUSES, DELIVERY_TYPES,
  deliveryLabel, paymentLabel, paymentTone, lifecycleLabel,
  listProductionTasks, productionStatusLabel,
  listOrderProductCostSnapshots, deleteProductCostSnapshot,
} from "@/lib/masterOrderApi";
import ApplyScenarioModal from "@/components/masterOrder/ApplyScenarioModal";
import { FinishedOrderBlock } from "@/components/masterOrder/FinishedOrderBlock";
import { PaymentsDrawer } from "@/components/masterOrder/PaymentsDrawer";
import { formatApiError } from "@/lib/api";

function ReadRow({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>{label}</div>
      <div className="mt-0.5 text-[13.5px]" style={{ color: "#2A1D14" }}>{value || <span style={{ color: "#B9A692" }}>—</span>}</div>
    </div>
  );
}

// Sprint MO-UX-Simplify: strip legacy 4-digit padding from order_number.
// Backend still stores 0001..0999 for old rows AND 10000+ for new ones —
// `f"{seq:04d}"` uses zfill semantics that don't truncate past 4 digits.
// Display always shows a plain integer with no leading zeros.
const stripPad = (n) => {
  if (n == null) return "—";
  const s = String(n).replace(/^0+/, "");
  return s || "0";
};

export default function MOOrderDetail() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);

  const [order, setOrder] = useState(null);
  const [client, setClient] = useState(null);
  const [items, setItems] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [costingByItem, setCostingByItem] = useState({}); // orderItemId -> currentCosting|null
  const [summary, setSummary] = useState(null);
  const [creatingCostingFor, setCreatingCostingFor] = useState(null);
  const [productionTasks, setProductionTasks] = useState([]);
  const [applyingScenario, setApplyingScenario] = useState(false);
  const [servicePaymentsOpen, setServicePaymentsOpen] = useState(false);
  const [pcSnapshots, setPcSnapshots] = useState([]);
  const [paySummary, setPaySummary] = useState(null); // {sale_total_minor, net_paid_minor, remaining_minor, currency_code, is_total_reliable}
  // Sprint MO-UX-Simplify state (per-item editor + cancel modal)
  const [itemEditor, setItemEditor] = useState(null); // {mode:'create'|'edit', item?, draft}
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const ptMap = useMemo(() => Object.fromEntries(productTypes.map((p) => [p.id, p])), [productTypes]);

  const [form, setForm] = useState({});
  const initForm = (o) => setForm({
    event_type: o.event_type || "",
    event_date: o.event_date || null,
    event_time: (o.event_time || "").slice(0,5),
    guests_count: o.guests_count ?? "",
    wishes: o.wishes || "",
    dislikes: o.dislikes || "",
    allergies: o.allergies || "",
    design_notes: o.design_notes || "",
    budget: o.budget != null ? String(o.budget) : "",
    currency_code: o.currency_code || "EUR",
    business_status: o.business_status,
    lifecycle_status: o.lifecycle_status,
    delivery_type: o.delivery?.type || "",
    delivery_address: o.delivery?.address || "",
    delivery_date: o.delivery?.date || null,
    delivery_time_window: o.delivery?.time_window || "",
    delivery_cost: o.delivery?.cost != null ? String(o.delivery.cost) : "",
    delivery_notes: o.delivery?.notes || "",
    recipient_name: o.recipient?.name || "",
    recipient_phone: o.recipient?.phone || "",
    recipient_notes: o.recipient?.notes || "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const o = await getOrder(orderId);
      setOrder(o);
      initForm(o);
      const [c, its, pts] = await Promise.all([getClient(o.client_id).catch(() => null), listOrderItems(o.id), listProductTypes()]);
      setClient(c); setItems(its); setProductTypes(pts);
      // Fetch current costing per item + order-level summary + production tasks in parallel.
      const [currents, sum, prodTasks, snapshots, ps] = await Promise.all([
        Promise.all(its.map((it) => getCostingCurrent(it.id).catch(() => null))),
        orderCostingSummary(o.id).catch(() => null),
        listProductionTasks({ order_id: o.id }).catch(() => []),
        listOrderProductCostSnapshots(o.id).catch(() => []),
        getOrderPaymentsSummary(o.id).catch(() => null),
      ]);
      const map = {};
      its.forEach((it, k) => { map[it.id] = currents[k] || null; });
      setCostingByItem(map);
      setSummary(sum);
      setProductionTasks(prodTasks);
      setPcSnapshots(snapshots || []);
      setPaySummary(ps);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    load();
    /* eslint-disable-next-line */
  }, [orderId, aLoad, mode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        event_type: form.event_type || null,
        event_date: form.event_date,
        event_time: form.event_time || null,
        guests_count: form.guests_count === "" ? null : parseInt(form.guests_count, 10),
        wishes: form.wishes || null,
        dislikes: form.dislikes || null,
        allergies: form.allergies || null,
        design_notes: form.design_notes || null,
        budget: form.budget || null,
        currency_code: form.currency_code,
        business_status: form.business_status,
        lifecycle_status: form.lifecycle_status,
      };
      if (form.delivery_type) {
        payload.delivery = {
          type: form.delivery_type,
          address: form.delivery_address || null,
          date: form.delivery_date || null,
          time_window: form.delivery_time_window || null,
          cost: form.delivery_cost || null,
          notes: form.delivery_notes || null,
        };
      }
      if (form.recipient_name || form.recipient_phone || form.recipient_notes) {
        payload.recipient = {
          name: form.recipient_name || null,
          phone: form.recipient_phone || null,
          notes: form.recipient_notes || null,
        };
      }
      const o = await updateOrder(orderId, payload);
      setOrder(o);
      initForm(o);
      setEditing(false);
      toast.success("Изменения сохранены");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeItem = async (it) => {
    try {
      await deleteOrderItem(it.id);
      setItems((cur) => cur.filter((x) => x.id !== it.id));
      toast.success("Позиция удалена");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addItem = () => {
    // Sprint MO-UX-Simplify: «+ Позиция» opens a compact editor so the
    // new row is created empty and fully editable. We NEVER pre-copy the
    // previous item's product_type — every position is independent.
    if (!canWrite) { toast.error("Режим просмотра — создание недоступно"); return; }
    if (!productTypes.length) return toast.error("Сначала создайте тип продукта");
    setItemEditor({
      mode: "create",
      draft: {
        product_type_id: "", // user must pick explicitly
        quantity: 1,
        size_display: "",
        flavor_composition: "",
        guests: "",
        weight_kg: "",
        tiers: "",
      },
    });
  };

  const openItemEdit = (it) => {
    if (!canWrite) return;
    setItemEditor({
      mode: "edit",
      item: it,
      draft: {
        product_type_id: it.product_type_id || "",
        quantity: it.quantity ?? 1,
        size_display: it.size_display || "",
        flavor_composition: it.flavor_composition || "",
        // Cake-specific soft hints stashed in flavor_composition suffix — but
        // for edit mode we simply keep them blank; if user re-enters they
        // become part of description text on save.
        guests: "",
        weight_kg: "",
        tiers: "",
      },
    });
  };

  const submitItemEditor = async () => {
    const d = itemEditor.draft;
    if (!d.product_type_id) return toast.error("Выберите тип продукта");
    const q = parseInt(d.quantity, 10);
    if (!q || q <= 0) return toast.error("Количество должно быть больше нуля");
    // Cake-specific soft data merged into flavor_composition as a small
    // sidecar so we don't need a schema change for a demo hint.
    const extras = [];
    if ((d.guests || "").trim())    extras.push(`Гостей: ${d.guests}`);
    if ((d.weight_kg || "").trim()) extras.push(`Вес торта: ${d.weight_kg} кг`);
    if ((d.tiers || "").trim())     extras.push(`Ярусов: ${d.tiers}`);
    const flavor = [
      (d.flavor_composition || "").trim(),
      extras.join(" · "),
    ].filter(Boolean).join(" · ") || null;
    try {
      if (itemEditor.mode === "create") {
        const it = await createOrderItem({
          order_id: orderId,
          product_type_id: d.product_type_id,
          quantity: q,
          size_display: d.size_display || null,
          flavor_composition: flavor,
        });
        setItems((cur) => [...cur, it]);
        setCostingByItem((m) => ({ ...m, [it.id]: null }));
        toast.success("Позиция добавлена");
      } else {
        const updated = await updateOrderItem(itemEditor.item.id, {
          product_type_id: d.product_type_id,
          quantity: q,
          size_display: d.size_display || null,
          flavor_composition: flavor,
        });
        setItems((cur) => cur.map((x) => x.id === updated.id ? updated : x));
        toast.success("Позиция обновлена");
      }
      setItemEditor(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Sprint MO-UX-Simplify: cancel & restore (business_status only).
  // The endpoint keeps the row + all payments + snapshots — we only flip
  // the status and stamp reason/cancelled_at (server derives cancelled_at).
  const cancelOrder = async () => {
    setCancelling(false);
    try {
      const updated = await updateOrder(order.id, {
        business_status: "cancelled",
        cancellation_reason: (cancelReason || "").trim() || null,
      });
      setOrder(updated);
      setCancelReason("");
      toast.success("Заказ отменён. Данные сохранены в истории.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreOrder = async () => {
    try {
      const updated = await updateOrder(order.id, {
        business_status: "request",
      });
      setOrder(updated);
      toast.success("Заказ возвращён в работу");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openOrCreateCosting = async (item) => {
    const cur = costingByItem[item.id];
    if (cur) { nav(`/master-order/costings/${cur.id}`); return; }
    if (!canWrite) { toast.error("Режим просмотра — создание недоступно"); return; }
    setCreatingCostingFor(item.id);
    try {
      // Re-check in case another tab created one meanwhile.
      const existing = await getCostingCurrent(item.id).catch(() => null);
      if (existing) { nav(`/master-order/costings/${existing.id}`); return; }
      const draft = await createCostingDraft({
        order_id: orderId, order_item_id: item.id,
        recipe_components: [], packaging_lines_input: [], labor_lines_input: [], other_cost_lines: [],
        tech_loss_percent: "0",
      });
      toast.success("Черновик калькуляции создан");
      nav(`/master-order/costings/${draft.id}`);
    } catch (e) {
      toast.error(mapCostingError(e) || formatApiError(e));
    } finally { setCreatingCostingFor(null); }
  };

  return (
    <MOShell active="orders" title={order ? `№ ${stripPad(order.order_number)}` : "Заказ"} subtitle={client?.name || ""}>
      {aLoad ? <LoadingBlock /> :
       mode === "no_access" || mode === "demo" ? <NoAccessScreen /> :
       loading ? <LoadingBlock /> :
       notFound || !order ? (
         <div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
           <div className="text-[16px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Заказ не найден</div>
           <button className="mt-4 h-10 px-4 rounded-full text-[13px]"
                   style={{ background: "#4A3728", color: "#F7EFE0" }} onClick={() => nav("/master-order/orders")}>
             К списку заказов
           </button>
         </div>
       ) : (
        <div className="space-y-4">
          {order.business_status === "cancelled" && (
            <div className="rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-2"
                 style={{ background: "#F9EFE7", boxShadow: "inset 0 0 0 1px #EFD5C8", color: "#8A3F1E" }}
                 data-testid="mo-order-cancelled-banner">
              <div className="text-[12.5px] flex items-center gap-2">
                <Ban size={13} strokeWidth={1.7} />
                <span>Заказ отменён{order.cancelled_at ? ` · ${new Date(order.cancelled_at).toLocaleDateString("ru-RU")}` : ""}
                  {order.cancellation_reason ? ` · ${order.cancellation_reason}` : ""}
                </span>
              </div>
            </div>
          )}

          {/* Sprint MO-UX-Simplify: at-a-glance finance card (Стоимость / Получено / Осталось) */}
          {paySummary && (
            <div className="rounded-xl bg-white px-4 py-3"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                 data-testid="mo-order-finance-card">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Стоимость заказа</div>
                  <div className="mt-1 text-[15px] tabular-nums" style={{ color: paySummary.is_total_reliable ? "#2A1D14" : "#B9A692" }}
                       data-testid="mo-order-finance-total">
                    {paySummary.is_total_reliable && paySummary.sale_total_minor != null
                      ? `${(paySummary.sale_total_minor / 100).toFixed(2).replace(".", ",")} ${paySummary.currency_code}`
                      : "рассчитывается"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Получено</div>
                  <div className="mt-1 text-[15px] tabular-nums" style={{ color: "#2A6B48" }}
                       data-testid="mo-order-finance-received">
                    {((paySummary.net_paid_minor || 0) / 100).toFixed(2).replace(".", ",")} {paySummary.currency_code}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Осталось</div>
                  <div className="mt-1 text-[15px] tabular-nums"
                       style={{ color: paySummary.is_total_reliable
                                  ? ((paySummary.remaining_minor || 0) <= 0 ? "#2A6B48" : "#B26B4A")
                                  : "#B9A692" }}
                       data-testid="mo-order-finance-remaining">
                    {paySummary.is_total_reliable && paySummary.remaining_minor != null
                      ? `${(paySummary.remaining_minor / 100).toFixed(2).replace(".", ",")} ${paySummary.currency_code}`
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <button onClick={() => nav("/master-order/orders")}
                    className="text-[12.5px] inline-flex items-center gap-1.5"
                    style={{ color: "#8B6F4B" }}
                    data-testid="mo-order-back">
              <ArrowLeft size={13} strokeWidth={1.8} /> К списку заказов
            </button>
            {canWrite && !editing && order.business_status !== "cancelled" && (
              <div className="flex items-center gap-2">
                <button onClick={() => setCancelling(true)}
                        className="h-9 px-3 rounded-full text-[12px] inline-flex items-center gap-1.5"
                        style={{ boxShadow: "inset 0 0 0 1px #EFD5C8", color: "#8A3F1E", background: "#FFFFFF" }}
                        data-testid="mo-order-cancel-btn">
                  <Ban size={12} strokeWidth={1.7} /> Отменить заказ
                </button>
                <button onClick={() => setEditing(true)}
                        className="h-9 px-4 rounded-full text-[12.5px] inline-flex items-center gap-1.5"
                        style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}
                        data-testid="mo-order-edit">
                  <Edit2 size={12} strokeWidth={1.8} /> Редактировать
                </button>
              </div>
            )}
            {canWrite && !editing && order.business_status === "cancelled" && (
              <button onClick={restoreOrder}
                      className="h-9 px-3 rounded-full text-[12px] inline-flex items-center gap-1.5"
                      style={{ boxShadow: "inset 0 0 0 1px #C7A574", color: "#4A3728", background: "#FBF5EA" }}
                      data-testid="mo-order-restore-btn">
                <Undo2 size={12} strokeWidth={1.7} /> Вернуть в работу
              </button>
            )}
          </div>

          {/* Header block */}
          <div className="rounded-xl p-5"
               style={{
                 background: order.business_status === "cancelled"
                   ? "linear-gradient(120deg, #F3E4DA, #F9EFE7)"
                   : "linear-gradient(120deg, #F5EBD8, #FBF5EA)",
                 boxShadow: "inset 0 0 0 1px #EFE4D0",
               }}
               data-testid="mo-order-header">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[10.5px] tracking-[0.24em] uppercase" style={{ color: "#8B6F4B" }}>Заказ</div>
                <div className="mt-0.5 text-[28px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                     data-testid="mo-order-number-display">
                  № {stripPad(order.order_number)}
                </div>
                <div className="text-[13px] mt-1" style={{ color: "#4A3728" }}>
                  {client ? (
                    <button className="underline underline-offset-2 hover:opacity-80"
                            onClick={() => nav(`/master-order/clients/${client.id}`)}
                            data-testid="mo-order-client-link">
                      {client.name}
                    </button>
                  ) : "—"}
                  {" · "}{order.event_date}{order.event_time ? ` · ${order.event_time.slice(0,5)}` : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  {/* Primary: business/workflow stage */}
                  <StatusBadge status={order.business_status} testid="mo-order-business-badge" />
                  {/* Separate: payment */}
                  <StatusBadge tone={paymentTone(order.payment_status)} testid="mo-order-payment-badge">
                    {paymentLabel(order.payment_status)}
                  </StatusBadge>
                  <span className="text-[11px] px-2.5 py-0.5 rounded-full" style={{ background: "#FFFFFF", color: "#5B4326", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                    {order.currency_code}
                  </span>
                  {order.budget != null && (
                    <span className="text-[13px] tabular-nums px-3 py-1 rounded-full" style={{ background: "#4A3728", color: "#F7EFE0" }}>
                      {order.budget} {order.currency_code}
                    </span>
                  )}
                </div>
                {/* Secondary: lifecycle (subtle) */}
                <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }} data-testid="mo-order-lifecycle-label">
                  Состояние: {lifecycleLabel(order.lifecycle_status)}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-4">
            {/* Left: main sections */}
            <div className="space-y-4">
              {/* Order / event */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Заказ</div>
                {!editing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ReadRow label="Тип события" value={order.event_type} />
                    <ReadRow label="Гостей" value={order.guests_count ?? ""} />
                    <ReadRow label="Валюта" value={order.currency_code} />
                    <ReadRow label="Бюджет" value={order.budget != null ? `${order.budget} ${order.currency_code}` : ""} />
                    <ReadRow label="Рабочий этап" value={<StatusBadge status={order.business_status} />} />
                    <ReadRow label="Состояние заказа" value={lifecycleLabel(order.lifecycle_status)} />
                    <ReadRow label="Оплата" value={<StatusBadge tone={paymentTone(order.payment_status)}>{paymentLabel(order.payment_status)}</StatusBadge>} />
                    <ReadRow label="Дата события" value={`${order.event_date}${order.event_time ? " " + order.event_time.slice(0,5) : ""}`} />
                    <div className="md:col-span-2"><ReadRow label="Пожелания / дизайн" value={order.wishes} /></div>
                    {client?.allergies_common && (
                      <div className="md:col-span-2"
                           data-testid="mo-order-client-allergies">
                        <div className="text-[10.5px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>
                          Из карточки клиента
                        </div>
                        <div className="mt-0.5 text-[13px]" style={{ color: "#5B4326" }}>
                          {client.allergies_common}
                        </div>
                      </div>
                    )}
                    <div className="md:col-span-2"><ReadRow label="Дополнительные ограничения для этого заказа" value={order.allergies} /></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Дата события *">
                      <DateField value={form.event_date} onChange={(v) => set("event_date", v)} testid="mo-order-edit-date" />
                    </Field>
                    <Field label="Время">
                      <input type="time" value={form.event_time} onChange={(e) => set("event_time", e.target.value)}
                             className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                             style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                    </Field>
                    <Field label="Тип события"><Input value={form.event_type} onChange={(v) => set("event_type", v)} testid="mo-order-edit-event-type" /></Field>
                    <Field label="Гостей"><Input value={String(form.guests_count ?? "")} onChange={(v) => set("guests_count", v.replace(/\D/g, ""))} /></Field>
                    <Field label="Валюта *"><CurrencyField value={form.currency_code} onChange={(v) => set("currency_code", v)} /></Field>
                    <Field label="Бюджет"><Input value={form.budget} onChange={(v) => set("budget", v.replace(/[^\d.,]/g, ""))} testid="mo-order-edit-budget" /></Field>
                    <Field label="Рабочий этап">
                      <select value={form.business_status} onChange={(e) => set("business_status", e.target.value)}
                              className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                              data-testid="mo-order-edit-status">
                        {BUSINESS_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Состояние заказа">
                      <select value={form.lifecycle_status} onChange={(e) => set("lifecycle_status", e.target.value)}
                              className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                              data-testid="mo-order-edit-lifecycle">
                        {LIFECYCLE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </Field>
                    <div className="md:col-span-2"><Field label="Пожелания / дизайн">
                      <textarea value={form.wishes} onChange={(e) => set("wishes", e.target.value)}
                                placeholder="Оформление, надписи, декор, вес, референсы, важные детали заказа."
                                className="w-full h-24 p-3 rounded-lg text-[13px] outline-none resize-y"
                                style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                    </Field></div>
                    {client?.allergies_common && (
                      <div className="md:col-span-2 rounded-lg px-3 py-2"
                           style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                        <div className="text-[10.5px] tracking-[0.22em] uppercase mb-0.5" style={{ color: "#8B6F4B" }}>
                          Из карточки клиента
                        </div>
                        <div className="text-[12.5px]" style={{ color: "#5B4326" }}>{client.allergies_common}</div>
                      </div>
                    )}
                    <div className="md:col-span-2"><Field label="Дополнительные ограничения для этого заказа">
                      <textarea value={form.allergies} onChange={(e) => set("allergies", e.target.value)}
                                placeholder="Только новые ограничения именно этого заказа."
                                className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-y"
                                style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                    </Field></div>
                    <div className="md:col-span-2 flex justify-end gap-2 pt-1">
                      <button onClick={() => { setEditing(false); initForm(order); }} className="h-10 px-4 rounded-full text-[12.5px]"
                              style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
                        Отменить
                      </button>
                      <button onClick={save} disabled={saving}
                              className="h-10 px-5 rounded-full text-[12.5px] inline-flex items-center gap-2 disabled:opacity-60"
                              style={{ background: "#4A3728", color: "#F7EFE0" }}
                              data-testid="mo-order-save">
                        <Save size={13} strokeWidth={1.8} /> {saving ? "Сохраняем..." : "Сохранить"}
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* Items */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-order-items">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Позиции</div>
                  {canWrite && (
                    <button onClick={addItem} className="h-9 px-3 rounded-full text-[12.5px] inline-flex items-center gap-1.5"
                            style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}
                            data-testid="mo-order-add-item">
                      <Plus size={13} strokeWidth={1.8} /> Позиция
                    </button>
                  )}
                </div>
                {items.length === 0 ? (
                  <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Пока нет позиций.</div>
                ) : (
                  <div className="space-y-3">
                    {items.map((it, idx) => {
                      return (
                      <div key={it.id} className="rounded-lg p-3" style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                           data-testid={`mo-order-item-${it.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>Позиция #{idx + 1}</div>
                          <div className="flex items-center gap-1">
                            {canWrite && (
                              <button onClick={() => openItemEdit(it)}
                                      className="w-8 h-8 rounded-full grid place-items-center hover:bg-white"
                                      style={{ color: "#4A3728" }} aria-label="Редактировать позицию"
                                      data-testid={`mo-order-item-edit-${it.id}`}>
                                <Edit2 size={12} strokeWidth={1.7} />
                              </button>
                            )}
                            {canWrite && (
                              <button onClick={() => removeItem(it)}
                                      className="w-8 h-8 rounded-full grid place-items-center hover:bg-white"
                                      style={{ color: "#8A3F1E" }} aria-label="Удалить">
                                <Trash2 size={13} strokeWidth={1.7} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                          <ReadRow label="Тип продукта" value={ptMap[it.product_type_id]?.name || "—"} />
                          <ReadRow label="Количество" value={it.quantity} />
                          <ReadRow label="Размер" value={it.size_display} />
                          <div className="md:col-span-3"><ReadRow label="Состав / вкус" value={it.flavor_composition} /></div>
                        </div>
                        {/* Sprint MO-UX-Simplify: two clean CTAs replace the legacy costing UI. */}
                        <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2"
                             style={{ borderColor: "#EFE4D0" }}
                             data-testid={`mo-item-actions-${it.id}`}>
                          <button onClick={() => nav(`/master-order/recipe-recalc?order_id=${order.id}&position_id=${it.id}`)}
                                  className="h-9 px-4 rounded-full text-[12.5px] inline-flex items-center gap-1.5 whitespace-nowrap"
                                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
                                  data-testid={`mo-item-recipe-recalc-${it.id}`}>
                            <Scale size={12} strokeWidth={1.7} /> Расчёты
                          </button>
                          <button onClick={() => nav(`/master-order/product-cost?order_id=${order.id}&position_id=${it.id}`)}
                                  className="h-9 px-4 rounded-full text-[12.5px] inline-flex items-center gap-1.5 whitespace-nowrap"
                                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                                  data-testid={`mo-item-product-cost-${it.id}`}>
                            <Calculator size={12} strokeWidth={1.7} /> Себестоимость изделия
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Attached product-cost snapshots (Sprint Product-Cost-Attach) */}
              {pcSnapshots.length > 0 && (
                <section className="rounded-xl bg-white p-5"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                         data-testid="mo-order-pc-snapshots">
                  <div className="text-[17px] mb-3"
                       style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                    Себестоимость изделия
                  </div>
                  <div className="space-y-2">
                    {pcSnapshots.map((s) => (
                      <PCSnapshotRow key={s.id}
                                     snap={s}
                                     canWrite={canWrite}
                                     onRemove={async () => {
                                       if (!window.confirm("Удалить сохранённый расчёт из заказа?")) return;
                                       try {
                                         await deleteProductCostSnapshot(s.id);
                                         setPcSnapshots((prev) => prev.filter((x) => x.id !== s.id));
                                       } catch (e) { toast.error(formatApiError(e)); }
                                     }} />
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Right: delivery, recipient, meta */}
            <div className="space-y-4">
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Получение</div>
                {!editing ? (
                  order.delivery ? (
                    <div className="space-y-3 text-[13px]" style={{ color: "#2A1D14" }}>
                      <div className="inline-flex items-center gap-1.5 text-[12.5px]" style={{ color: "#5B4326" }}>
                        {order.delivery.type === "pickup" ? <Handshake size={12} /> : <Truck size={12} />} {deliveryLabel(order.delivery.type)}
                      </div>
                      <ReadRow label="Адрес" value={order.delivery.address} />
                      <ReadRow label="Дата / окно" value={[order.delivery.date, order.delivery.time_window].filter(Boolean).join(" · ")} />
                      <ReadRow label="Стоимость" value={order.delivery.cost != null ? `${order.delivery.cost} ${order.currency_code}` : ""} />
                      <ReadRow label="Заметка" value={order.delivery.notes} />
                    </div>
                  ) : <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Способ получения не задан.</div>
                ) : (
                  <div className="space-y-3">
                    <Field label="Способ">
                      <select value={form.delivery_type} onChange={(e) => set("delivery_type", e.target.value)}
                              className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
                        <option value="">Не выбрано</option>
                        {DELIVERY_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </Field>
                    {form.delivery_type && (
                      <>
                        <Field label="Адрес"><Input value={form.delivery_address} onChange={(v) => set("delivery_address", v)} /></Field>
                        <Field label="Дата доставки"><DateField value={form.delivery_date} onChange={(v) => set("delivery_date", v)} /></Field>
                        <Field label="Окно"><Input value={form.delivery_time_window} onChange={(v) => set("delivery_time_window", v)} placeholder="11:00 - 13:00" /></Field>
                        <Field label="Стоимость"><Input value={form.delivery_cost} onChange={(v) => set("delivery_cost", v.replace(/[^\d.,]/g, ""))} /></Field>
                      </>
                    )}
                  </div>
                )}
              </section>

              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Получатель</div>
                {!editing ? (
                  order.recipient ? (
                    <div className="space-y-3">
                      <ReadRow label="Имя" value={order.recipient.name} />
                      <ReadRow label="Телефон" value={order.recipient.phone} />
                      <ReadRow label="Заметка" value={order.recipient.notes} />
                    </div>
                  ) : <div className="text-[12.5px]" style={{ color: "#8B6F4B" }}>Получатель — сам клиент.</div>
                ) : (
                  <div className="space-y-3">
                    <Field label="Имя"><Input value={form.recipient_name} onChange={(v) => set("recipient_name", v)} /></Field>
                    <Field label="Телефон"><Input value={form.recipient_phone} onChange={(v) => set("recipient_phone", v)} /></Field>
                  </div>
                )}
              </section>

              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                       data-testid="mo-order-production-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                    Производство
                  </div>
                  <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}
                       data-testid="mo-order-production-count">
                    {productionTasks.length} · {productionTasks.filter((t) => t.status === "done").length} готово
                  </div>
                </div>
                {productionTasks.length === 0 ? (
                  <div className="text-[12px]" style={{ color: "#8B6F4B" }}
                       data-testid="mo-order-production-empty">
                    Для этого заказа этапы производства ещё не заведены.
                  </div>
                ) : (() => {
                  const next = [...productionTasks]
                    .filter((t) => t.status !== "done")
                    .sort((a, b) =>
                      (a.scheduled_date.localeCompare(b.scheduled_date)) ||
                      ((a.start_time || "").localeCompare(b.start_time || "")) ||
                      (a.sort_order - b.sort_order))[0] || null;
                  return next ? (
                    <div className="rounded-lg px-3 py-2.5" style={{ background: "#FDF7EC" }}
                         data-testid="mo-order-production-next">
                      <div className="text-[10.5px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>
                        Следующий этап
                      </div>
                      <div className="text-[13.5px] mt-0.5" style={{ color: "#2A1D14" }}>
                        {next.title}
                      </div>
                      <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                        {next.scheduled_date}
                        {next.start_time ? ` · ${next.start_time.slice(0,5)}` : ""}
                        {" · "}{productionStatusLabel(next.status)}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg px-3 py-2.5 text-[12.5px]"
                         style={{ background: "#EAE2CE", color: "#4A3728" }}
                         data-testid="mo-order-production-all-done">
                      Все этапы готовы.
                    </div>
                  );
                })()}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => nav(`/master-order/production?order_id=${order.id}`)}
                          className="h-9 px-3.5 rounded-full text-[12.5px] inline-flex items-center gap-1.5"
                          style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          data-testid="mo-order-production-open">
                    Открыть производство <ArrowRight size={12} />
                  </button>
                  {canWrite && (
                    <>
                      <button onClick={() => nav(`/master-order/production?order_id=${order.id}&new=1`)}
                              className="h-9 px-3.5 rounded-full text-[12.5px] inline-flex items-center gap-1.5"
                              style={{ background: "#4A3728", color: "#F7EFE0" }}
                              data-testid="mo-order-production-add">
                        <Plus size={12} /> Создать вручную
                      </button>
                      <button onClick={() => setApplyingScenario(true)}
                              className="h-9 px-3.5 rounded-full text-[12.5px] inline-flex items-center gap-1.5"
                              style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                              data-testid="mo-order-production-apply-scenario">
                        Из моего сценария
                      </button>
                    </>
                  )}
                </div>
              </section>

              <FinishedOrderBlock
                order={order}
                items={items}
                ptMap={ptMap}
                canWrite={canWrite}
                onSaved={async (updatedOrder) => {
                  if (updatedOrder) {
                    setOrder(updatedOrder);
                    initForm(updatedOrder);
                  } else {
                    // Payments changed — refetch the order so the derived
                    // payment_status badge (and downstream summaries) refresh.
                    try {
                      const fresh = await getOrder(order.id);
                      setOrder(fresh);
                      initForm(fresh);
                    } catch (e) { /* silent */ }
                  }
                  // Also refresh items so weight updates reflect immediately
                  try {
                    const its = await listOrderItems(order.id);
                    setItems(its);
                  } catch (e) { /* silent */ }
                }}
              />

              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Служебная</div>
                <div className="space-y-2 text-[11.5px]" style={{ color: "#5B4326" }}>
                  <div>Создан: {order.created_at?.slice(0,10)}</div>
                  <div>Обновлён: {order.updated_at?.slice(0,10)}</div>
                  <div className="flex items-center gap-1.5">
                    Оплата:
                    <StatusBadge tone={paymentTone(order.payment_status)}>
                      {paymentLabel(order.payment_status)}
                    </StatusBadge>
                  </div>
                </div>
                <div className="mt-3">
                  <button onClick={() => setServicePaymentsOpen(true)}
                          className="h-8 px-3 rounded-full text-[12px] inline-flex items-center gap-1"
                          style={{ background: "#F7F1E7", color: "#4A3728",
                                   boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          data-testid="mo-order-open-payments">
                    Оплаты
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-1.5 text-[10.5px]" style={{ color: "#8B6F4B" }}>
                  <div>Документы → скоро</div>
                  <div>История → скоро</div>
                </div>
              </section>

              {servicePaymentsOpen && (
                <PaymentsDrawer
                  order={order}
                  canWrite={canWrite}
                  onClose={() => setServicePaymentsOpen(false)}
                  onOrderChanged={async () => {
                    try {
                      const fresh = await getOrder(order.id);
                      setOrder(fresh);
                      initForm(fresh);
                    } catch (e) { /* silent */ }
                  }}
                />
              )}
            </div>
          </div>
        </div>
       )
      }
      {applyingScenario && order && (
        <ApplyScenarioModal
          orders={[order]}
          preselectedOrderId={order.id}
          onClose={() => setApplyingScenario(false)}
          onApplied={async () => {
            try {
              const fresh = await listProductionTasks({ order_id: order.id });
              setProductionTasks(fresh);
            } catch (e) { /* silent */ }
          }}
          onGoCreateScenario={() => nav("/master-order/production/scenarios")}
        />
      )}

      {/* Sprint MO-UX-Simplify: per-item editor modal (create / edit) */}
      {itemEditor && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
             style={{ background: "rgba(35,23,16,0.55)" }}
             onClick={(e) => { if (e.target === e.currentTarget) setItemEditor(null); }}
             data-testid="mo-order-item-editor">
          <div className="w-full md:max-w-md bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5"
               style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh", overflowY: "auto" }}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>ANOKINA STUDIO</div>
                <div className="text-[19px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  {itemEditor.mode === "create" ? "Новая позиция" : "Редактировать позицию"}
                </div>
              </div>
              <button onClick={() => setItemEditor(null)}
                      className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                      style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      aria-label="Закрыть"
                      data-testid="mo-order-item-editor-close">
                <X size={13} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Тип продукта</div>
                <select value={itemEditor.draft.product_type_id}
                        onChange={async (e) => {
                          const v = e.target.value;
                          if (v === "__new__") {
                            const name = (window.prompt("Введите название нового типа (например: Пряники, Леденцы, Топпер)", "") || "").trim();
                            if (!name) return;
                            try {
                              const pt = await createProductType({ name });
                              setProductTypes((cur) => [...cur, pt]);
                              setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, product_type_id: pt.id } }));
                              toast.success(`Тип «${pt.name}» создан`);
                            } catch (err) { toast.error(formatApiError(err)); }
                            return;
                          }
                          setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, product_type_id: v } }));
                        }}
                        className="w-full h-11 px-3 rounded-md bg-white text-base outline-none"
                        style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                        data-testid="mo-order-item-editor-type">
                  <option value="">Выберите тип продукта…</option>
                  {productTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                  <option value="__new__">＋ Новый тип продукта…</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Количество</div>
                  <input type="text" inputMode="numeric"
                         value={itemEditor.draft.quantity}
                         onChange={(e) => setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, quantity: e.target.value.replace(/\D/g, "") } }))}
                         className="w-full h-11 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                         data-testid="mo-order-item-editor-qty" />
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Размер</div>
                  <input type="text"
                         value={itemEditor.draft.size_display}
                         onChange={(e) => setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, size_display: e.target.value } }))}
                         placeholder="например, 20 см"
                         className="w-full h-11 px-3 rounded-md bg-white text-base outline-none"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                         data-testid="mo-order-item-editor-size" />
                </div>
              </div>
              <div>
                <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Состав / вкус</div>
                <textarea value={itemEditor.draft.flavor_composition}
                          onChange={(e) => setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, flavor_composition: e.target.value } }))}
                          placeholder="Например: шоколад, ваниль, малина."
                          rows={3}
                          className="w-full p-3 rounded-md bg-white text-base outline-none resize-y"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                          data-testid="mo-order-item-editor-flavor" />
              </div>

              {/* Sprint MO-UX-Simplify: cake-specific soft hints. Rendered
                  only when the selected product type name contains «торт». */}
              {(() => {
                const ptName = (productTypes.find((p) => p.id === itemEditor.draft.product_type_id)?.name || "").toLowerCase();
                const isCake = ptName.includes("торт");
                if (!isCake) return null;
                const guests = parseInt(itemEditor.draft.guests, 10);
                const tiers = parseInt(itemEditor.draft.tiers, 10);
                const recommend = Number.isFinite(guests) && guests > 0
                  ? { min: (guests * 0.15).toLocaleString("ru-RU"), max: (guests * 0.2).toLocaleString("ru-RU") }
                  : null;
                return (
                  <div className="space-y-3 rounded-lg p-3"
                       style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                       data-testid="mo-order-item-editor-cake-hints">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <div className="text-[10px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Гостей</div>
                        <input value={itemEditor.draft.guests} type="text" inputMode="numeric"
                               onChange={(e) => setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, guests: e.target.value.replace(/\D/g, "") } }))}
                               className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                               data-testid="mo-order-item-editor-guests" />
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Вес, кг</div>
                        <input value={itemEditor.draft.weight_kg} type="text" inputMode="decimal"
                               onChange={(e) => setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, weight_kg: e.target.value.replace(/[^\d.,]/g, "") } }))}
                               className="w-full h-10 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                               data-testid="mo-order-item-editor-weight" />
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Ярусы</div>
                        <select value={itemEditor.draft.tiers}
                                onChange={(e) => setItemEditor((cur) => ({ ...cur, draft: { ...cur.draft, tiers: e.target.value } }))}
                                className="w-full h-10 px-3 rounded-md bg-white text-base outline-none"
                                style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                                data-testid="mo-order-item-editor-tiers">
                          <option value="">—</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4+</option>
                        </select>
                      </div>
                    </div>
                    {recommend && (
                      <div className="rounded-md p-2 bg-white"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                           data-testid="mo-order-item-editor-recommend">
                        <div className="text-[10px] tracking-[0.18em] uppercase" style={{ color: "#8B6F4B" }}>
                          Рекомендуемый вес торта
                        </div>
                        <div className="text-[15px] mt-0.5 tabular-nums"
                             style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}>
                          {recommend.min}–{recommend.max} кг
                        </div>
                        <div className="text-[10.5px] mt-1" style={{ color: "#8B6F4B" }}>
                          Ориентир 150–200 г на гостя. Точный вес зависит от состава и формата подачи.
                        </div>
                      </div>
                    )}
                    {Number.isFinite(tiers) && tiers >= 2 && (
                      <div className="rounded-md p-2 bg-white"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                           data-testid="mo-order-item-editor-tier-hint">
                        <div className="text-[10px] tracking-[0.18em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
                          Для многоярусного торта
                        </div>
                        <div className="text-[11.5px] leading-snug" style={{ color: "#4A3728" }}>
                          Проверьте конструкцию и заранее предупредите клиента: внутренние опоры,
                          промежуточные подложки между ярусами, усиленное основание, подходящая
                          транспортировочная коробка, безопасная перевозка, съёмный или несъедобный
                          декор, который нужно убрать перед нарезкой.
                        </div>
                        <div className="text-[11px] mt-1.5" style={{ color: "#8B6F4B" }}>
                          Если декор нужно снять перед нарезкой, обязательно сообщите это клиенту.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setItemEditor(null)}
                        className="h-10 px-4 rounded-full text-[13px]"
                        style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                  Отмена
                </button>
                <button onClick={submitItemEditor}
                        className="h-10 px-5 rounded-full text-[13px]"
                        style={{ background: "#4A3728", color: "#F7EFE0" }}
                        data-testid="mo-order-item-editor-save">
                  {itemEditor.mode === "create" ? "Добавить" : "Сохранить"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sprint MO-UX-Simplify: cancel-order confirmation modal */}
      {cancelling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
             style={{ background: "rgba(35,23,16,0.55)" }}
             onClick={(e) => { if (e.target === e.currentTarget) setCancelling(false); }}
             data-testid="mo-order-cancel-modal">
          <div className="w-full max-w-sm bg-[#FBF5EA] rounded-2xl p-5"
               style={{ boxShadow: "0 8px 32px rgba(35,23,16,0.15)" }}>
            <div className="text-[10.5px] tracking-[0.28em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
              Заказ №{order ? stripPad(order.order_number) : ""}
            </div>
            <div className="text-[19px] mb-2"
                 style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              Отменить этот заказ?
            </div>
            <p className="text-[12.5px] mb-3" style={{ color: "#5B4326" }}>
              Заказ, клиент, платежи, себестоимость, история и файлы будут сохранены. Отменённый заказ не попадёт в активное производство. Вы сможете вернуть его в работу позже.
            </p>
            <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Причина (не обязательно)</div>
            <textarea value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value.slice(0, 1000))}
                      placeholder="Например: по просьбе клиента; форс-мажор."
                      rows={3}
                      className="w-full p-3 rounded-md bg-white text-base outline-none resize-y"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-order-cancel-reason" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setCancelling(false); setCancelReason(""); }}
                      className="h-10 px-4 rounded-full text-[13px]"
                      style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-order-cancel-modal-cancel">
                Не отменять
              </button>
              <button onClick={cancelOrder}
                      className="h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-1.5"
                      style={{ background: "#8A3F1E", color: "#F7EFE0" }}
                      data-testid="mo-order-cancel-modal-confirm">
                <Ban size={13} strokeWidth={1.7} /> Отменить заказ
              </button>
            </div>
          </div>
        </div>
      )}
    </MOShell>
  );
}


/* ─────────── Product-cost snapshot row (read-only inside Order) ───────────
 * Financial layer captured at attach time. Never recomputed here — later
 * changes in the Workbook or price catalog do NOT rewrite this snapshot. */
function PCSnapshotRow({ snap, canWrite, onRemove }) {
  const cur = snap.currency_code || "EUR";
  const fmtNum = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n.toFixed(2).replace(".", ",");
  };
  const money = (v) => { const s = fmtNum(v); return s === null ? null : `${s} ${cur}`; };
  const addedAt = snap.added_at
    ? new Date(snap.added_at).toLocaleDateString("ru-RU",
        { day: "numeric", month: "long", year: "numeric" })
    : null;
  const marginStr = fmtNum(snap.margin_percent);
  const profitN = snap.profit === null || snap.profit === undefined
    ? null : Number(snap.profit);
  const marginN = snap.margin_percent === null || snap.margin_percent === undefined
    ? null : Number(snap.margin_percent);
  return (
    <div className="rounded-lg p-3"
         style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid={`mo-order-pc-snap-${snap.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}
               data-testid="mo-order-pc-snap-title">{snap.title}</div>
          {addedAt && (
            <div className="text-[11px] mt-0.5" style={{ color: "#8B6F4B" }}>
              Добавлено {addedAt}
            </div>
          )}
        </div>
        {canWrite && (
          <button onClick={onRemove}
                  aria-label="Удалить расчёт из заказа"
                  className="w-7 h-7 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid={`mo-order-pc-snap-remove-${snap.id}`}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
        {money(snap.full_cost_total) !== null && (
          <Row label="Полная себестоимость" value={money(snap.full_cost_total)} />
        )}
        {money(snap.sale_price) !== null && (
          <Row label="Цена продажи" value={money(snap.sale_price)} />
        )}
        {profitN !== null && (
          <Row label="Прибыль с изделия"
               value={money(snap.profit)}
               color={profitN < 0 ? "#B26B4A" : "#2A6B48"} />
        )}
        {marginN !== null && (
          <Row label="Маржа"
               value={`${marginStr}%`}
               color={marginN < 0 ? "#B26B4A" : "#2A6B48"} />
        )}
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[12px]" style={{ color: "#5B4326" }}>{label}</span>
      <span className="tabular-nums text-[13px]"
            style={{ color: color || "#2A1D14" }}>
        {value}
      </span>
    </div>
  );
}
